import type { Customer as CustomerWithNullableAddress } from 'Customers/types'
import { SimpleModal, Box } from '@sequencehq/core-components'
import { Dashboardv20240730CommonModels } from '@sequencehq/api/dashboard/v20240730'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { UpdateCustomerError } from 'Customer/hooks/useCustomerRoot'
import { CustomerFormErrorBanner } from 'Customer/components/CustomerFormErrorBanner'
import { useCustomerContext } from 'Customer/hooks/useCustomerContext'
import { useCustomerRoot } from 'Customer/hooks/useCustomerRoot'
import {
  CustomerFormFields,
  CustomerFormFieldsData,
  useCustomerFormFields
} from 'Customer/components/CustomerFormFields'
import {
  customFieldsFromRecord,
  customFieldsToRecord
} from '@sequencehq/core-components'
import { CountriesAlpha2 } from '@sequencehq/api'
import { statesForCountry } from '@sequencehq/api/utils/commonEnums'
import { emailToName } from 'CustomerContacts/utils/emailToName'
import { useLogEvent } from 'lib/postHog/useLogEvent'
import { getBillingAddress } from '@sequencehq/api/utils/commonModels'

interface Props {
  onClose: () => void
  onSuccess?: (customer: CustomerWithNullableAddress) => void
}

export const EditCustomerForm: FC<Props> = props => {
  const {
    initialData,
    showParentCustomer,
    updateError,
    showValidationErrors,
    failedSubmitCount,
    errorBannerRef,
    setIsValid,
    handleFormChange,
    handleUpdate,
    customerId
  } = useEditCustomerForm(props)

  const { fields, customProperties } = useCustomerFormFields({
    initialData,
    onChange: handleFormChange,
    onValidationStateChange: setIsValid,
    customerId,
    showValidationErrors,
    // The contacts section isn't rendered in this drawer — contacts are
    // managed on the customer page itself.
    sectionValidation: { contacts: false }
  })

  return (
    <SimpleModal
      isOpen
      data-testid="editCustomer.drawer"
      variant="drawer"
      title="Edit customer"
      cancelButtonText="Cancel"
      submitButtonText="Update customer"
      submittingButtonText="Updating customer..."
      onClose={props.onClose}
      onSubmit={handleUpdate}
    >
      <Box ref={errorBannerRef} marginBottom={updateError ? '16px' : 0}>
        <CustomerFormErrorBanner operation="UPDATE" error={updateError} />
      </Box>
      <CustomerFormFields failedSubmitCount={failedSubmitCount}>
        <CustomerFormFields.AccountInformation
          data-testid="editCustomerForm.accountInformation"
          legalNameField={fields.legalName}
          autoFocus
        />
        <CustomerFormFields.BillingInformation
          data-testid="editCustomerForm.billingInformation"
          addressFields={fields}
        />
        <CustomerFormFields.Tax
          data-testid="editCustomerForm.tax"
          taxStatusField={fields.taxStatus}
          taxRegistrationsField={fields.taxRegistrations}
          defaultCountry={fields['address.country'].value}
          defaultState={fields['address.state'].value}
        />
        <CustomerFormFields.OtherInformation
          data-testid="editCustomerForm.otherInformation"
          labelField={fields.label}
          parentIdField={showParentCustomer ? fields.parentId : undefined}
          aliasesField={fields.aliases}
          customProperties={customProperties}
        />
      </CustomerFormFields>
    </SimpleModal>
  )
}

const useEditCustomerForm = (props: Props) => {
  const { logEvent } = useLogEvent()
  const {
    customer: customerDetails,
    customerAliases,
    taxRegistrations,
    refetchCustomer,
    refetchCustomerAliases
  } = useCustomerContext()

  const {
    functions: { editCustomer }
  } = useCustomerRoot({
    refetchCustomer,
    refetchCustomerAliases
  })

  useEffect(() => {
    logEvent({ eventName: 'edit_customer_form_opened' })
    void refetchCustomer()
  }, [refetchCustomer])

  const [updateError, setUpdateError] = useState<UpdateCustomerError>()
  const [isValid, setIsValid] = useState(true)
  const [failedSubmitCount, setFailedSubmitCount] = useState(0)
  const showValidationErrors = failedSubmitCount > 0
  const formDataRef = useRef<CustomerFormFieldsData | null>(null)
  const errorBannerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (updateError) {
      errorBannerRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [updateError])

  const initialData = useMemo((): CustomerFormFieldsData => {
    const billingAddress = getBillingAddress(customerDetails.addresses)

    return {
      legalName: customerDetails.legalName ?? '',
      contacts: (customerDetails.contacts ?? []).map(c => ({
        id: c.id,
        name: c.name ?? '',
        email: c.email,
        purposes: c.purposes,
        notificationSubscriptions: c.notificationSubscriptions
      })),
      address: {
        // No default country: an address-less customer opens with a blank
        // address so it can be saved without one. Do not fabricate a country.
        country: (billingAddress?.country ?? '') as CountriesAlpha2 | '',
        line1: billingAddress?.line1 ?? '',
        line2: billingAddress?.line2,
        town: billingAddress?.town ?? '',
        state: billingAddress?.state,
        postcode: billingAddress?.postcode ?? ''
      },
      taxStatus:
        (customerDetails.taxStatus as Dashboardv20240730CommonModels.Tax.TaxStatus) ??
        'TAX_EXEMPT',
      aliases: customerAliases.length
        ? customerAliases.map(({ value }) => value)
        : [''],
      label: customerDetails.label ?? '',
      parentId: customerDetails?.organizations[0]?.owner?.id,
      // Use the server id as the entry id so this memo produces deep-equal
      // output when the mount-time refetch returns unchanged data — a random
      // id would force useForm to reinitialize and wipe in-progress edits.
      taxRegistrations: taxRegistrations.map(reg => ({
        id: reg.id,
        registrationId: reg.id,
        country: reg.country as CountriesAlpha2,
        state: reg.state,
        identifier: reg.taxIdentifier
      })),
      customProperties: customFieldsFromRecord(customerDetails.customProperties)
    }
  }, [customerDetails, customerAliases, taxRegistrations])

  const showParentCustomer = !customerDetails?.organizations.some(
    org => org.owner?.id === customerDetails.id
  )

  const handleFormChange = useCallback((data: CustomerFormFieldsData) => {
    formDataRef.current = data
  }, [])

  const handleUpdate = async () => {
    setUpdateError(undefined)

    if (!isValid) {
      setFailedSubmitCount(count => count + 1)
      return false
    }

    const data = formDataRef.current ?? initialData

    // A blank country means the address was cleared/never set — send the
    // customer without an address rather than an empty-string address.
    const country = data.address.country
    const countryStates = country
      ? statesForCountry(country as CountriesAlpha2)
      : []
    const address = country
      ? {
          country: country as CountriesAlpha2,
          line1: data.address.line1.trim(),
          line2: data.address.line2?.trim() || undefined,
          town: data.address.town.trim(),
          state:
            data.address.state && countryStates.includes(data.address.state)
              ? data.address.state
              : undefined,
          postcode: data.address.postcode.trim()
        }
      : undefined

    const result = await editCustomer(customerDetails.id, {
      legalName: data.legalName.trim(),
      contacts: data.contacts.map(contact => ({
        name: contact.name ?? emailToName(contact.email),
        email: contact.email,
        purposes: contact.purposes,
        notificationSubscriptions: contact.notificationSubscriptions
      })),
      address,
      taxStatus: data.taxStatus as Dashboardv20240730CommonModels.Tax.TaxStatus,
      label: data.label || undefined,
      parentId: data.parentId || undefined,
      customerAliases: data.aliases,
      taxRegistrations: data.taxRegistrations,
      customProperties: customFieldsToRecord(data.customProperties)
    })

    if (!result) {
      return false
    }

    if (result?.error) {
      setUpdateError(result.error)
      return false
    }

    if (result?.customer) {
      props.onSuccess?.(result.customer)
      return true
    }

    return false
  }

  return {
    initialData,
    showParentCustomer,
    updateError,
    showValidationErrors,
    failedSubmitCount,
    errorBannerRef,
    setIsValid,
    handleFormChange,
    handleUpdate,
    customerId: customerDetails.id
  }
}
